// In-game HUD: HP bar, timer, team scores, kill feed area.

import type { Plane } from '../plane/Plane';
import type { GameState } from '../core/GameState';

export class HUD {
  root: HTMLDivElement;
  hpFill: HTMLDivElement;
  hpLabel: HTMLDivElement;
  timer: HTMLDivElement;
  scoreBlue: HTMLDivElement;
  scoreRed: HTMLDivElement;
  killFeedRoot: HTMLDivElement;
  matchBanner: HTMLDivElement;

  constructor() {
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '40',
      font: '13px/1.3 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif',
    } as CSSStyleDeclaration);

    // Top center: score + timer.
    const top = document.createElement('div');
    Object.assign(top.style, {
      position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
      display: 'flex', alignItems: 'center', gap: '14px',
      background: 'rgba(8,12,28,0.55)', border: '1px solid rgba(180,200,255,0.22)',
      padding: '6px 14px', borderRadius: '20px', color: '#dde', letterSpacing: '1px',
    } as CSSStyleDeclaration);
    this.scoreBlue = document.createElement('div');
    this.scoreBlue.style.color = '#7dccff';
    this.scoreBlue.style.fontWeight = '600';
    this.scoreBlue.textContent = 'BLUE 0';
    this.timer = document.createElement('div');
    this.timer.style.color = '#fff';
    this.timer.style.minWidth = '54px';
    this.timer.style.textAlign = 'center';
    this.timer.textContent = '3:00';
    this.scoreRed = document.createElement('div');
    this.scoreRed.style.color = '#ff8e80';
    this.scoreRed.style.fontWeight = '600';
    this.scoreRed.textContent = '0 RED';
    top.appendChild(this.scoreBlue);
    top.appendChild(this.timer);
    top.appendChild(this.scoreRed);
    this.root.appendChild(top);

    // Bottom center: HP bar.
    const hp = document.createElement('div');
    Object.assign(hp.style, {
      position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
      width: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    } as CSSStyleDeclaration);
    this.hpLabel = document.createElement('div');
    this.hpLabel.style.color = '#fff';
    this.hpLabel.style.fontSize = '11px';
    this.hpLabel.style.letterSpacing = '1px';
    this.hpLabel.textContent = 'HP 3 / 3';
    const hpTrack = document.createElement('div');
    Object.assign(hpTrack.style, {
      width: '100%', height: '6px', background: 'rgba(0,0,0,0.5)',
      border: '1px solid rgba(180,200,255,0.3)', borderRadius: '3px', overflow: 'hidden',
    } as CSSStyleDeclaration);
    this.hpFill = document.createElement('div');
    Object.assign(this.hpFill.style, {
      width: '100%', height: '100%', background: 'linear-gradient(90deg,#5fffaa,#7dccff)',
      transition: 'width .15s ease',
    } as CSSStyleDeclaration);
    hpTrack.appendChild(this.hpFill);
    hp.appendChild(this.hpLabel);
    hp.appendChild(hpTrack);
    this.root.appendChild(hp);

    // Right side: kill feed.
    this.killFeedRoot = document.createElement('div');
    Object.assign(this.killFeedRoot.style, {
      position: 'absolute', top: '54px', right: '12px',
      display: 'flex', flexDirection: 'column', gap: '4px',
      maxWidth: '40vw',
    } as CSSStyleDeclaration);
    this.root.appendChild(this.killFeedRoot);

    // Center banner for match begin/end.
    this.matchBanner = document.createElement('div');
    Object.assign(this.matchBanner.style, {
      position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)',
      color: '#fff', fontSize: '32px', letterSpacing: '4px', fontWeight: '200',
      textShadow: '0 2px 16px #000', opacity: '0', transition: 'opacity .4s',
    } as CSSStyleDeclaration);
    this.root.appendChild(this.matchBanner);

    document.body.appendChild(this.root);
  }

  showBanner(text: string, durationMs = 1800): void {
    this.matchBanner.textContent = text;
    this.matchBanner.style.opacity = '1';
    if (durationMs > 0) {
      setTimeout(() => { this.matchBanner.style.opacity = '0'; }, durationMs);
    }
  }

  hideBanner(): void {
    this.matchBanner.style.opacity = '0';
  }

  update(player: Plane, state: GameState, planes: Plane[]): void {
    const snap = state.snapshot(planes);
    const mins = Math.floor(snap.timeLeft / 60);
    const secs = Math.floor(snap.timeLeft % 60);
    this.timer.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    this.scoreBlue.textContent = `BLUE ${snap.blue}`;
    this.scoreRed.textContent = `${snap.red} RED`;
    this.hpLabel.textContent = `HP ${player.hp} / ${player.maxHp}`;
    const pct = player.maxHp > 0 ? (player.hp / player.maxHp) * 100 : 0;
    this.hpFill.style.width = `${pct}%`;
    this.hpFill.style.background = pct > 66
      ? 'linear-gradient(90deg,#5fffaa,#7dccff)'
      : pct > 33 ? 'linear-gradient(90deg,#ffd55f,#ff8a5f)' : 'linear-gradient(90deg,#ff5560,#ff90a0)';
  }

  destroy(): void {
    this.root.remove();
  }
}
